import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {css} from 'aphrodite';
import {findIconDefinition} from '@fortawesome/fontawesome-svg-core';
import {ToolTipCell} from 'components/ToolTipCell';
import './styles.scss';
import {CoverageSummary as CoverageSummaryNew} from '../Coverage/Cell/CoverageSummary/index';
import {DocumentsCell} from '../Coverage/Cell/DocumentsCell';
import {HighlightRowCell} from '../Coverage/Cell/HighlightRowCell';
import {PdfLink} from '../Coverage/Cell/PdfLink';
import {createColumnHelper} from '@tanstack/react-table';
import {
  faInfoCircle,
  faFile as faFileSolid,
} from '@fortawesome/fontawesome-pro-solid';
import {renderMultifieldString} from 'libs/salmon-utils';

const columnHelper = createColumnHelper();

/**
 * Resolves which column gets the default sort for the DynamicCoverage table.
 *
 * Site JSON: optional `settings.table.defaultSort`: `{ columnProperty, direction?: "asc"|"desc" }`
 * (`columnProperty` must match a column's `property` and that column must be `sortable`). If
 * `defaultSort` is absent or invalid, uses the first sortable column in `table.columns` order,
 * ascending.
 *
 * Maps to `columnDef.initialSort` for TabularDataBasic/Header (false = asc, true = desc).
 *
 * @param {Object} tableConfig - `settings.table`
 * @returns {{ targetProperty: string|null, initialSort: boolean|undefined }}
 */
export const applyDefaultSortOrder = (tableConfig) => {
  const columns = tableConfig?.columns ?? [];
  const defaultSort = tableConfig?.defaultSort;

  if (
    defaultSort &&
    typeof defaultSort === 'object' &&
    defaultSort.columnProperty != null &&
    defaultSort.columnProperty !== ''
  ) {
    const match = columns.find((c) => c.property === defaultSort.columnProperty);
    if (match?.sortable) {
      const dir = String(defaultSort.direction ?? 'asc').toLowerCase();
      return {
        targetProperty: match.property,
        initialSort: dir === 'desc',
      };
    }

  }

  const firstSortable = columns.find((c) => c.sortable);
  if (firstSortable) {
    return {targetProperty: firstSortable.property, initialSort: false};
  }

  return {targetProperty: null, initialSort: undefined};
};

const planTypeHeaderRender = ({settings, styleSheet, label}) => {
  if (settings.content.planTypeHelpHover.length) {
    return (
      <span>
        {label}
        <ToolTipCell
          text={settings.content.planTypeHelpHover}
          placeholder={(<FontAwesomeIcon data-testid="plantype-tooltip" icon={faInfoCircle}/>)}
          placement="bottom"
          styleSheet={styleSheet}
          id="plantype-tooltip"
          ariaLabel={'More information about plan types'}
        />
      </span>
    )
  }

  return label;
}

export const getDynamicCoverageColumns = ({props}) => {
  const {settings, styleSheet} = props;
  const {targetProperty, initialSort: defaultInitialSort} = applyDefaultSortOrder(
    settings.table
  );

  return settings.table.columns.map((column) => {
    const styleName = `tableColumn${column.property}`;
    const cellClass = styleSheet[styleName] ? css(styleSheet[styleName]) : '';

    const isDefaultSortColumn =
      defaultInitialSort !== undefined &&
      targetProperty != null &&
      column.property === targetProperty;

    const dataTableColumn = {
      enableSorting: column.sortable,
      enableColumnFilter: false,
      sortDescFirst: false,
      // initialSort: applied on mount in TabularDataBasic/Header (see JSDoc on applyDefaultSortOrder)
      ...(isDefaultSortColumn && {initialSort: defaultInitialSort}),
      meta: {className: cellClass},
      size: 0,
      header: () => {
        if (column.property === 'planType') {
          return planTypeHeaderRender({...props, label: column.label});
        }

        return column.label;
      },
      cell: renderDynamicColumnCell(
        column,
        props
      )
    };

    return columnHelper.accessor(column.property, dataTableColumn);
  })
}

/**
 * Renders a dynamic column cell based on the column type
 * @param {Object} column - The column object
 * @param {Object} props - The props object
 * @returns {JSX.Element} The rendered column cell
 */
export const renderDynamicColumnCell = (
  column,
  props
) => {
  return ({cell, row}) => {
    const value = cell.getValue();
    const rowData = row.original;

    // Auto-detect type if not specified
    const type = column.type || (Array.isArray(value) ? 'array' : typeof value === 'object' ? 'object' : 'string');

    switch (type) {
      case 'array':
        return getArrayCellRender({...props, value, rowData, column, meta: column.meta || {separator: ', '}});
      case 'object':
        return getObjectCellRender({...props, value, rowData, column});
      case 'coverageSummary':
        return getCoverageSummaryTypeCellRender({...props, value, rowData, column});
      case 'documents':
        return getDocumentsCellRender({...props, value, column, rowData});
      case 'contacts':
        return getNewContactsCellRender({...props, value, rowData, column});
      case 'highlightRow':
        return getHighlightRowCellRender({...props, value, rowData, column});
      case 'customDisplay':
        return getCustomDisplayCellRender({...props, value, column});
      case 'pdfLink':
        return getPdfLinkCellRender({...props, value, rowData, column});
      case 'medicalNecessityChecklist':
        return getMedicalNecessityChecklistCellRender({...props, rowData, column});
      case 'string':
        if (value == null) {
          return '';
        }
        return getFilterMappingCellRender({value: String(value), column, settings: props.settings});
      default:
        return ''
    }
  }
}

/**
 * Renders a custom display cell based on the column type
 * @param {Object} value - The value of the cell
 * @param {Object} column - The column object
 * @returns {JSX.Element} The rendered cell
 */
const getFilterMappingCellRender = ({value, column, settings}) => {
  const filterEntry = (settings?.filterLayout || []).find(
    (f) => f.filter === 'dynamic' && f.fieldName === column.property,
  );
  return filterEntry?.mappings?.[value] ?? value;
};

export const getCustomDisplayCellRender = ({value, column}) => {
  const customDisplay = column.meta?.customDisplay || {};

  return customDisplay.mappings?.[value] ?? value;
}

/**
 * Renders an array cell based on the column type
 */
export const getArrayCellRender = ({styleSheet, rowData, value, column, meta}) => {
  if (!Array.isArray(value) || value.length === 0) {
    return '';
  }

  const separator = meta?.separator || ', ';

  // Handle array of objects (e.g., documentTypes with {id, name})
  const displayValue = value.length > 0 && typeof value[0] === 'object' && value[0].name
    ? value.map((item) => item.name).join(separator)
    : value.join(separator);

  if (meta?.tooltip && value.length > 5) {
    const tooltipId = column?.property ? `tooltip-${rowData.id}-${column.property}` : `tooltip-${rowData.id}`;
    return (
      <ToolTipCell
        text={displayValue}
        placement={'bottom'}
        styleSheet={styleSheet}
        placeholder={`${displayValue.slice(0, 20)} ...`}
        id={tooltipId}
        ariaLabel={displayValue}
      />
    );
  }

  return (<span>{displayValue}</span>);
}

/**
 * Renders an object cell based on the column type
 */
export const getObjectCellRender = ({value, rowData, column}) => {
  if (!value || typeof value !== 'object') {
    return '';
  }

  // Handle array of objects (like documentTypes)
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '';
    }
    if (value[0]?.name) {
      // Extract names from objects
      return <span>{value.map((item) => item.name).join(', ')}</span>;
    }
  }

  // For plain objects, stringify
  return <span>{JSON.stringify(value)}</span>;
}

/**
 * Renders a link cell based on the column type
 */
export const getDocumentsCellRender = ({settings, styleSheet, value, column, rowData}) => {
  return <DocumentsCell
    settings={settings}
    styleSheet={styleSheet}
    value={value}
    column={column}
    rowData={rowData}
  />;
}

/**
 * Renders a new contacts cell based on the column type
 * @param {Object} value - The value of the cell
 * @param {Object} column - The column object
 * @param {Object} rowData - The row data
 * @param {Object} styleSheet - The style sheet object
 * @returns {JSX.Element} The rendered cell
 */
export const getNewContactsCellRender = ({value, column, rowData, styleSheet}) => {
  const contactsList = column.meta?.contacts?.fields ?? [];
  const contacts = contactsList.map((contactMeta) => {
    const icon = findIconDefinition({prefix: contactMeta.icon.prefix, iconName: contactMeta.icon.name});

    let contactValue = null;
    if (rowData.fields && Array.isArray(rowData.fields)) {
      const contact = rowData.fields.find((field) => field.fieldName === contactMeta.fieldName);
      contactValue = contact?.value;
    } else if (rowData[contactMeta.fieldName] !== undefined) {
      contactValue = rowData[contactMeta.fieldName];
    }

    return (
      <ToolTipCell
        key={contactMeta.fieldName}
        text={contactMeta.title}
        placement={'bottom'}
        styleSheet={styleSheet}
        placeholder={
          <span key={contactMeta.fieldName}>
            <FontAwesomeIcon icon={icon} flip="horizontal"/>
            {contactValue ? contactValue : contactMeta.default}<br/>
          </span>
        }
        id={`contact-tooltip-${contactMeta.fieldName}`}
      />
    );
  });

  return (
    <div key="contacts">
      {contacts}
    </div>
  );
}

/**
 * Renders a coverage summary type cell based on the column type
 */
export const getCoverageSummaryTypeCellRender = ({value, rowData, column, styleSheet}) => {
  return <CoverageSummaryNew
    criteria={value}
    column={column}
    rowData={rowData}
    styleSheet={styleSheet}
  />;
}

export const shouldHighlightRow = (rowData) => {
  const documents = rowData.fields?.find((field) => field.fieldName === 'documents');
  return (
    Boolean(documents) &&
    documents.value.length === 1 &&
    documents.value[0].documentTypes.length === 0
  );
};

export const getHighlightRowCellRender = ({settings, styleSheet, value, rowData}) => {
  if (!shouldHighlightRow(rowData)) {
    return value;
  }

  return <HighlightRowCell
    settings={settings}
    styleSheet={styleSheet}
    value={value}
    rowData={rowData}
  />;
}

/**
 * Renders a medical necessity checklist cell. On click, serializes coverage data to localStorage
 * and opens the MedicalNecessityChecklist page in a new tab.
 */
export const getMedicalNecessityChecklistCellRender = ({rowData, settings, lastRefreshedDate, column}) => {
  const handleClick = () => {
    const medicalNecessity = column.meta.medicalNecessity;

    // Replace [[reviewDate]] placeholder with the actual last-refreshed date.
    let disclaimer = medicalNecessity.disclaimer;
    if (lastRefreshedDate) {
      disclaimer = disclaimer.replace(/\[\[(.*)\]\]/, lastRefreshedDate);
    }

    //mapping medicalNecessity fields to include the value from rowData and applying default if value is not present
    const fields = medicalNecessity.fields.map((el) => {
      const apiField = (rowData.fields ?? []).find((f) => f.fieldName === el.fieldName);
      const data = apiField?.value;
      return {
        ...el,
        text: Array.isArray(data)
          ? (data.length > 0 ? data.sort().join(', ') : el.default)
          : (data ? data : el.default),
      };
    });

    // Serialize the checklist payload to localStorage so the page opened in the
    // new tab can retrieve it by ID without needing an extra API call.
    const coverageData = {
      fillInTheBlanks: medicalNecessity.fillInTheBlanks,
      title: renderMultifieldString(medicalNecessity.title, rowData, medicalNecessity.allStates),
      fields,
      disclaimer,
      medicalNecessityStyles: settings.styles.medicalNecessityChecklist,
      header: {
        logo: settings.header.productLogo,
        style: settings.styles.headerLogo,
      },
      expiry: new Date().getTime() + 3600000, // Expire the data in 1 hour.
    };

    const sID = Math.floor(Math.random() * 1000000);
    localStorage.setItem(`medicalNecessityChecklist-${sID}`, JSON.stringify(coverageData));
    window.open(`medicalNecessityChecklist?id=${sID}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <a
      id={rowData.id}
      data-testid="open-medical-coverage-link"
      onClick={handleClick}
    >
      <FontAwesomeIcon icon={faFileSolid} size={'2x'} />
    </a>
  );
};

export const getPdfLinkCellRender = ({rowData, column, styleSheet}) => {
  return <PdfLink
    rowData={rowData}
    column={column}
    styleSheet={styleSheet}
  />;
};